import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export default function UpgradeModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [featureUrl, setFeatureUrl] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleUpgradeRequired = (event) => {
      const msg = event.detail?.message || 'Diese Funktion erfordert den Pro-Plan. Bitte upgraden Sie Ihren Account.';
      const url = event.detail?.feature || '';
      setMessage(msg);
      setFeatureUrl(url);
      setIsOpen(true);
    };

    window.addEventListener('upgrade-required', handleUpgradeRequired);
    return () => {
      window.removeEventListener('upgrade-required', handleUpgradeRequired);
    };
  }, []);

  const handleUpgradeClick = () => {
    setIsOpen(false);
    navigate('/pricing');
  };

  const getFeatureTitle = () => {
    if (featureUrl.includes('rent-increase')) return t('sweep.upgrade.rentIncreaseGenerator');
    if (featureUrl.includes('bank-transactions') || featureUrl.includes('banking')) return t('sweep.upgrade.bankSync');
    if (featureUrl.includes('invoices')) return t('sweep.upgrade.invoices');
    if (featureUrl.includes('apartments') || featureUrl.includes('onboarding/cases')) return t('sweep.upgrade.unitLimit');
    return t('sweep.upgrade.premiumFeature');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex flex-col items-center justify-center text-center space-y-4">
          <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="w-6 h-6 animate-pulse" />
          </div>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-500" />
            {getFeatureTitle()}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm text-center">
            {message}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl p-4 text-sm mt-2">
          <p className="font-semibold text-emerald-800 dark:text-emerald-400 mb-2">{t('sweep.upgrade.proBenefits')}</p>
          <ul className="space-y-2 text-emerald-700 dark:text-emerald-300">
            <li className="flex items-center gap-2">
              <span className="text-emerald-500">✓</span> {t('sweep.upgrade.benefitRentIncrease')}
            </li>
            <li className="flex items-center gap-2">
              <span className="text-emerald-500">✓</span> {t('sweep.upgrade.benefitBankSync')}
            </li>
            <li className="flex items-center gap-2">
              <span className="text-emerald-500">✓</span> {t('sweep.upgrade.benefitInvoices')}
            </li>
            <li className="flex items-center gap-2">
              <span className="text-emerald-500">✓</span> {t('sweep.upgrade.benefitUnits')}
            </li>
          </ul>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={() => setIsOpen(false)} className="w-full sm:w-auto">
            {t('sweep.upgrade.later')}
          </Button>
          <Button onClick={handleUpgradeClick} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
            {t('sweep.upgrade.upgradeNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
